import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {PGPApi, PGPKeyDetails} from "components/src/api/admin/generated";
import {Pager} from "components/src/common/Pager";
import {RenderAlert} from "components/src/common/RenderAlert";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {SaveChangesRef} from "components/src/common/SaveChangesRef";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {SettingsLevel} from "components/src/settings/SettingsUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle, useState} from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useNavigate} from "react-router-dom";
import {PGPKeyRow} from "./PGPKeyRow";
import {PGPKeyTableHeader} from "./PGPKeyTableHeader";

const pgpApi = new PGPApi(ApiConfig.config());

interface PGPSigningKeyProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string
    setShouldSave: Dispatch<SetStateAction<boolean>>
    setSaving : Dispatch<SetStateAction<boolean>>
}

export const PGPSigningKey = forwardRef<SaveChangesRef | undefined, PGPSigningKeyProps> ((({
                settingsLevel,
                settingsLevelSubSelector,
                setShouldSave,
                setSaving} : PGPSigningKeyProps, ref) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The available signing keys
     */
    const [availableSigningKeys, setAvailableSigningKeys] = useState<PGPKeyDetails[]>();
    /**
     * The active signing key for user or domain
     */
    const [activeSigningKey, setActiveSigningKey] = useState<PGPKeyDetails>();
    /**
     * The selected signing key
     */
    const [selectedSigningKeyId, setSelectedSigningKeyId] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadSigningKeyCounter, setReloadSigningKeyCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReloadSigningKey = useCallback(() => setReloadSigningKeyCounter(counter => counter + 1), []);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Override the forwarded ref with the saveSettings method to allow the parent to save the modified settings
     */
    useImperativeHandle(ref, () => {
        return {
            saveChanges(){
                save();
            }
        }
    });

    /**
     * Load all signing keys
     */
    useEffect(() => {
        logger.debug("Getting available signing keys");

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await pgpApi.pGPControllerGetAvailableSigningKeysForEmail(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await pgpApi.pGPControllerGetAvailableSigningKeysForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (callback === undefined) {
            throw new Error("callback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        callback().then(response => {
                if (superseded) {
                    return;
                }

                setAvailableSigningKeys(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting available signing keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        t]);

    /**
     * Load active signing keys
     */
    useEffect(() => {
        logger.debug("Getting active signing key");

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await pgpApi.pGPControllerGetSigningKeyForUser(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await pgpApi.pGPControllerGetSigningKeyForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (callback === undefined) {
            throw new Error("callback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        callback().then(response =>
        {
            if (superseded) {
                return;
            }

            let key : PGPKeyDetails | undefined = response.data;
            // for some reason, openapi returns an empty key if the back-end returns null
            key = key?.sha256Fingerprint !== undefined ? key : undefined;

            setActiveSigningKey(key);
            setSelectedSigningKeyId(key !== undefined ? key.id : undefined);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting active signing key", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        reloadSigningKeyCounter,
        t]);

    /**
     * Save changes. This called by the parent component via a useImperativeHandle (see above)
     */
    const save = () =>
    {
        if (selectedSigningKeyId === undefined) {
            throw new Error("selectedSigningKeyId === undefined")
        }

        logger.debug("saving selected signing key", selectedSigningKeyId)

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await pgpApi.pGPControllerSetSigningKeyForUser(
                        settingsLevelSubSelector, selectedSigningKeyId);
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await pgpApi.pGPControllerSetSigningKeyForDomain(
                        settingsLevelSubSelector, selectedSigningKeyId);
                };
                break;
        }

        if (callback === undefined) {
            throw new Error("callback is undefined");
        }

        // tell the parent that this component is saving (to show a spinner)
        setSaving(true);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        callback().then(() => {
            setShouldSave(false);
            // force a reload of the signing key
            forceReloadSigningKey();
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error setting signing key", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() =>
        {
            setSaving(false);

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }

    /**
     * Called when a certificate is selected (i.e., when the checkbox is checked)
     */
    const onSelectKey = (id : string) =>
    {
        setSelectedSigningKeyId(id);

        // if the currently selected signing key is different from the new, save is required
        setShouldSave(id !== activeSigningKey?.id);
    }

    function getSigningKeys()
    {
        const keys: PGPKeyDetails[] = [];

        // add the activeSigningKey first to make it the first key
        if (activeSigningKey !== undefined && activeSigningKey !== null) {
            keys.push(activeSigningKey);
        }

        if (availableSigningKeys !== undefined) {
            // add all keys but skip the active signing key
            keys.push(...availableSigningKeys.filter(key => key.id !== activeSigningKey?.id));
        }

        return keys;
    }

    return (
        <>
            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table bordered hover>
                        <PGPKeyTableHeader/>
                        <tbody>
                        {getSigningKeys().map((key : PGPKeyDetails) => (
                            <PGPKeyRow key={key.sha256Fingerprint} pgpKey={key}>
                                {/* Add radio button to first column */}
                                <Form.Check
                                    name="signing-certificate"
                                    className="me-0"
                                    type="radio"
                                    value={key.id}
                                    checked={selectedSigningKeyId === key.id}
                                    onChange={(e) => onSelectKey(e.target.value)}
                                    inline
                                />
                            </PGPKeyRow>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-baseline mt-2 mb-2">
                <div>
                    <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>
                </div>
                <Pager itemCount={availableSigningKeys?.length ?? 0}
                       currentPage={currentPage}
                       setCurrentPage={setCurrentPage}
                       maxItemsPerPage={rowsPerPage}
                />
            </div>
        </>
    );
}))

// see https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/display-name.md why this is needed
PGPSigningKey.displayName="PGPSigningKey"